import React from 'react';
import { useIntl } from 'react-intl';
import MikaCard from '../../components/MikaCard/MikaCard';
import { SERVICING_STATUS_COLORS, SERVICING_PRIORITY_COLORS } from '../../constants/constants';

interface Props {
    data: any[];
    onEdit: (item: any) => void;
}

var hashColor = function(str: string): string {
    var hash = 0;
    for (var i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    var colors = ['#1e3a5f', '#2a5a8a', '#0891b2', '#7c3aed', '#059669', '#d97706', '#dc2626'];
    return colors[Math.abs(hash) % colors.length];
};

var fmtDate = function(d: string | null): string {
    if (!d) return '-';
    var dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return String(dt.getDate()).padStart(2, '0') + '/' + String(dt.getMonth() + 1).padStart(2, '0') + '/' + dt.getFullYear();
};

var ServicingCardGrid: React.FC<Props> = function({ data, onEdit }) {
    var intl = useIntl();
    var fm = function(id: string, defaultMessage: string, values?: Record<string, any>) {
        return intl.formatMessage({ id: id, defaultMessage: defaultMessage }, values);
    };

    var getStatusLabel = function(item: any) {
        var s = item?.status;
        if (s === 1) return fm('service.strip.registered', 'E regjistruar');
        if (s === 2) return fm('service.strip.diagnosing', 'Në diagnostikim');
        if (s === 3 || s === 'waiting') return fm('service.strip.waiting', 'Në pritje');
        if (s === 4) return fm('service.strip.repairing', 'Në riparim');
        if (s === 6) return fm('service.strip.completed', 'E përfunduar');
        return item?.statusLabel || '';
    };
    var getPriorityLabel = function(item: any) {
        var p = item?.priority;
        if (p === 1) return fm('service.priority.low', 'E ulët');
        if (p === 2) return fm('service.priority.normal', 'Normale');
        if (p === 3) return fm('service.priority.high', 'E lartë');
        if (p === 4) return fm('service.priority.urgent', 'Urgjente');
        return item?.priorityLabel || '';
    };
    var translateTypeName = function(name: string | null | undefined): string {
        if (!name) return '';
        var n = name.toLowerCase().trim();
        if (n === 'kontroll') return fm('service.type.kontroll', 'Kontroll');
        if (n.indexOf('thjesht') !== -1) return fm('service.type.servisimSimple', 'Servisim i thjeshtë');
        if (n.indexOf('komplikuar') !== -1) return fm('service.type.servisimComplex', 'Servisim i komplikuar');
        return name;
    };

    var formatActiveDurationString = function(val: string | null | undefined): string {
        if (!val) return '';
        var str = String(val);
        var dayMatch = str.match(/(\d+)\s*(dit|day|Tag|giorn|día|дена)/i);
        var hourMatch = str.match(/(\d+)\s*(or|hour|Std|ore|hora|час)/i);
        var minMatch = str.match(/(\d+)\s*(min|мин)/i);
        var days = dayMatch ? parseInt(dayMatch[1]) : 0;
        var hours = hourMatch ? parseInt(hourMatch[1]) : 0;
        var minutes = minMatch ? parseInt(minMatch[1]) : 0;
        if (days === 0 && hours === 0 && minutes === 0) return str;
        var parts: string[] = [];
        if (days > 0) parts.push(fm('installation.detail.duration.days', '{count} ditë', { count: days }));
        if (hours > 0) parts.push(fm('installation.detail.duration.hours', '{count} orë', { count: hours }));
        if (minutes > 0 || parts.length === 0) parts.push(fm('installation.detail.duration.minutes', '{count} min', { count: minutes }));
        return parts.join(', ');
    };

    if (!data || data.length === 0) {
        return (
            <div className="ccg-grid">
                <div className="ccg-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
                    </svg>
                    <span style={{ marginTop: 12, fontSize: 13 }}>{fm('service.empty.servicings', 'Nuk ka servisime')}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="ccg-grid">
            {data.map(function(item: any) {
                var statusColors = SERVICING_STATUS_COLORS[item.status] || { color: '#94a3b8', bg: '#f1f5f9' };
                var priorityColors = SERVICING_PRIORITY_COLORS[item.priority] || { color: '#3b82f6', bg: '#dbeafe' };
                var clientName = item.client?.name || '-';
                var initials = clientName.substring(0, 2).toUpperCase();

                return (
                    <MikaCard
                        key={item.id}
                        onClick={function() { onEdit(item); }}
                        header={
                            <div className="ccg-card-header">
                                <div className="ccg-avatar" style={{ background: hashColor(clientName) }}>{initials}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="ccg-name">{clientName}</div>
                                    <div className="ccg-company">{item.project?.name || '-'}</div>
                                </div>
                                <span className="ccg-status-badge" style={{ color: statusColors.color, background: statusColors.bg }}>
                                    {getStatusLabel(item)}
                                </span>
                            </div>
                        }
                        body={
                            <div className="ccg-card-body-content">
                                <div className="ccg-info-grid">
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label">{fm('service.col.priority', 'Prioriteti')}</span>
                                        <span className="ccg-info-value" style={{ color: priorityColors.color, fontWeight: 600 }}>{getPriorityLabel(item)}</span>
                                    </div>
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label">{fm('service.col.type', 'Lloji')}</span>
                                        <span className="ccg-info-value">{translateTypeName(item.servicingType?.name) || '-'}</span>
                                    </div>
                                    {item.isWarranty && (
                                        <div className="ccg-info-item">
                                            <span className="ccg-info-label">{fm('service.col.warranty', 'Garancia')}</span>
                                            <span className="ccg-info-value" style={{ color: item.isWarrantyActive ? '#22c55e' : '#94a3b8' }}>
                                                {item.isWarrantyActive ? fm('service.warranty.exportActive', 'Nën garanci') : fm('service.warranty.exportExpired', 'E skaduar')}
                                            </span>
                                        </div>
                                    )}
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label">{fm('service.col.technician', 'Tekniku')}</span>
                                        <span className="ccg-info-value">{item.technician?.name || '-'}</span>
                                    </div>
                                    <div className="ccg-info-item">
                                        <span className="ccg-info-label">{fm('service.col.activeDuration', 'Koha aktive')}</span>
                                        <span className="ccg-info-value">{item.activeDuration ? formatActiveDurationString(item.activeDuration) : '-'}</span>
                                    </div>
                                    {item.diagnosis && (
                                        <div className="ccg-info-item" style={{ gridColumn: '1 / -1' }}>
                                            <span className="ccg-info-label">{fm('service.col.diagnosis', 'Diagnostika')}</span>
                                            <span className="ccg-info-value">{item.diagnosis}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        }
                        footer={
                            <div className="ccg-card-footer">
                                <span className="ccg-date">{fmtDate(item.createdAt)}</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ padding: '2px 6px', borderRadius: 8, fontSize: 9, fontWeight: 600, color: priorityColors.color, background: priorityColors.bg }}>
                                        {getPriorityLabel(item)}
                                    </span>
                                </div>
                            </div>
                        }
                    />
                );
            })}
        </div>
    );
};

export default ServicingCardGrid;
